import {
  GraphData,
  GraphDataEdge,
  GraphDataNode,
  QualifiedFeatureFragment,
} from "fe-common/graphql";
import { cytoData, PathGraph } from "./graph";
import { hashQF } from "fe-common/logic";
import React, {
  forwardRef,
  MutableRefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { CustomGraphData, CustomScratchData } from "./graphalgo";
import { Core, EdgeSingular } from "cytoscape";
import { Box, Icon, IconButton } from "@mui/material";
import Typography from "@mui/material/Typography";
import Toolbar from "@mui/material/Toolbar";
import ToggleButton from "@mui/lab/ToggleButton";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { withStyles } from "@mui/styles";

export interface GraphAreaActions {
  focusFinding: (id) => void;
  setVertical: (f: boolean) => void;
  toggleISA: (f: boolean) => void;
  toggleExtraNodes: (f: boolean) => void;
  extraNodeCount: () => number;
  showUsedInComp: (f: boolean) => void;
  onLayoutReady: (f: (ref?: GraphAreaActions) => void) => void;
  resetFocus: () => void;
  isFocused: () => boolean;
}

export const GraphArea = React.memo(
  forwardRef(
    (
      {
        graph,
        disease,
        onNodeSelect,
        onEdgeSelect,
        onFocusChange,
        reverseColoring,
        emptyPlaceholder,
        wrapper,
        options,
        showIsA,
        defaultSelectedNodeId,
      }: {
        disease: QualifiedFeatureFragment | null;
        graph: GraphData | null;
        onNodeSelect?: (selection: boolean, node: GraphDataNode) => void;
        onEdgeSelect?: (
          selection: boolean,
          edges: {
            path: GraphDataEdge;
            source: GraphDataNode;
            target: GraphDataNode;
          }[],
        ) => void;
        onFocusChange?: (focused: boolean) => void;
        reverseColoring?: boolean;
        emptyPlaceholder?: React.ReactElement;
        wrapper?: React.ComponentType;
        options?: { dontRenderToolbar?: boolean; bgcolor?: string };
        showIsA?: boolean;
        defaultSelectedNodeId?: string;
      },
      ref,
    ) => {
      let pathsGraph = useRef<PathGraph>(null);
      let onLayoutReady = useRef<(ref?: GraphAreaActions) => null>(null);
      let [isFocused, setIsFocused] = useState(false);

      // handle events from clicks on the graph
      const receiveEvent = useCallback(
        (event) => {
          let select = event.op == "select" || event.op == "unselect";
          let layout = event.op == "layout";
          let focus = event.op == "focus";
          if (select) {
            if (event.type == "node") {
              onNodeSelect &&
                onNodeSelect(event.op == "select", event.obj.data());

              evt({
                category: CATEGORY.GRAPH,
                action: ACTION.NODE,
                label: event.obj.data()?.name || "MISSING DATA",
              });
              //TODO I removed the unselect edge event since it caused some performance problem
            } else if (event.type == "edge" && event.op == "select") {
              // convert edges to list
              let edges = unpackBundledEdges(event.obj);

              onEdgeSelect &&
                onEdgeSelect(
                  event.op == "select",
                  edges.map((obj) => ({
                    path: obj.data(),
                    source: obj.data(CustomGraphData.origSource)
                      ? (obj.cy() as Core)
                          .$(`#${obj.data(CustomGraphData.origSource)}`)
                          .data()
                      : obj.source().data(),
                    target: obj.data(CustomGraphData.origTarget)
                      ? (obj.cy() as Core)
                          .$(`#${obj.data(CustomGraphData.origTarget)}`)
                          .data()
                      : obj.target().data(),
                  })),
                );

              evt({
                category: CATEGORY.GRAPH,
                action: ACTION.EDGE,
                label: event.obj.data()?.id || "MISSING DATA",
              });
            }
          } else if (layout) {
            // let newFocusState = pathsGraph.current?.isFocused();
            // onFocusChange && onFocusChange(newFocusState);
            // setIsFocused(newFocusState);
            onLayoutReady &&
              onLayoutReady.current &&
              onLayoutReady.current((ref as any).current as GraphAreaActions);
          } else if (focus) {
            let newFocusState = event.value != null;
            onFocusChange && onFocusChange(newFocusState);
            setIsFocused(newFocusState);
          }
        },
        [onEdgeSelect, onNodeSelect],
      );

      // publish callbacks to allow toolbars to send actions to the graph
      useImperativeHandle<any, GraphAreaActions>(ref, () => ({
        focusFinding: (id) =>
          pathsGraph.current && pathsGraph.current.focusNodeByFinding(id),
        setVertical: (f: boolean) =>
          pathsGraph.current &&
          pathsGraph.current.addLayoutOption(
            f ? { direction: "DOWN" } : { direction: "RIGHT" },
          ),
        toggleISA: (f) =>
          pathsGraph.current && pathsGraph.current.collapseIsA(f),
        toggleExtraNodes: (f) =>
          pathsGraph.current && pathsGraph.current.toggleLowWeight(f),
        extraNodeCount: () =>
          pathsGraph.current && pathsGraph.current.getTruncatedNodeCount(),
        showUsedInComp: (f) =>
          pathsGraph.current && pathsGraph.current.showUsedInComp(f),
        onLayoutReady: (f: () => null) => (onLayoutReady.current = f),
        resetFocus: () => pathsGraph.current && pathsGraph.current.resetFocus(),
        isFocused: () => pathsGraph.current && pathsGraph.current.isFocused(),
      }));

      let [elements, setElements] = useState([]);
      useEffect(() => {
        if (graph) setElements(shapeGraphData(graph, disease));
      }, [graph]);

      if (!graph) {
        if (emptyPlaceholder) return emptyPlaceholder;
        return (
          <Box
            display="flex"
            flex={1}
            width={"100%"}
            height={"100%"}
            justifyItems="stretch"
            alignItems="center"
            alignContent={"center"}
            padding={30}
          >
            <Typography variant={"button"}>No Data{"  "}</Typography>
          </Box>
        );
      }

      const graphComponent = (
        <>
          <PathGraph
            elements={elements}
            ref={pathsGraph}
            onEvent={receiveEvent}
            reverseColoring={reverseColoring}
            bgcolor={options?.bgcolor}
            showIsA={showIsA}
            defaultSelectedNodeId={defaultSelectedNodeId}
          />
          {(!options || options.dontRenderToolbar !== true) && (
            <Toolbar variant="dense">
              {isFocused && (
                <LargeButton onClick={pathsGraph.current?.resetFocus}>
                  <LargeIcon color="primary">undo</LargeIcon>
                </LargeButton>
              )}
            </Toolbar>
          )}
        </>
      );

      if (wrapper) {
        const Wrapper = wrapper;
        return <Wrapper>{graphComponent}</Wrapper>;
      }

      return (
        <div
          style={{
            display: "flex",
            flexGrow: 1,
            backgroundColor: "blue",
            height: "100%",
            width: "100%",
          }}
        >
          <div
            style={{
              minHeight: "10rem",
              height: "100%",
              width: "100%",
              flexDirection: "row",
              alignItems: "stretch",
              backgroundColor: "yellow",
            }}
          >
            {graphComponent}
          </div>
        </div>
      );
    },
  ),
);

function shapeGraphData(graph: GraphData, disease: QualifiedFeatureFragment) {
  let graphs = [graph];
  if (graphs && graphs.length > 0) {
    let { elements } = cytoData({ start: disease as any, graphs: graphs });

    // mark our findings to the graph to make it easy to find in the graph
    // todo remove this code and use the SourceQF id instead
    elements.forEach((e) => {
      if (e.data.sourceQF) {
        e.data.foundInPatient = true;
        e.data.findingid = hashQF(e.data.sourceQF);
        e.data.sourceQF = e.data.sourceQF.id;
      }
      if (e.data.feature) {
        e.data.foundInPatient = true;
        e.data.findingId = hashQF(e.data.feature);
      }
    });
    return elements;
  } else return [];
}

function unpackBundledEdges(e: EdgeSingular) {
  // convert edges to list
  let edges = [];
  // was this edge overridden forward or backward?
  let forward = e.data(CustomGraphData.subGraphForward);
  let subgraph = e.scratch(CustomScratchData.subGraph);
  if (subgraph) {
    subgraph.edges().forEach((e) => {
      edges.push(...unpackBundledEdges(e));
    });
  }
  if (forward) {
    edges.push(e);
  } else {
    edges.unshift(e);
  }
  return edges;
}

export function GraphToolBar({
  graphref,
}: {
  graphref: MutableRefObject<GraphAreaActions>;
}) {
  let [verticalOrient, setVerticalOrient] = useState(true);
  let [showA, setShowA] = useState(true);
  let [showE, setShowE] = useState(false);
  let [showComp, setShowCalc] = useState(false);
  let [isFocused, setIsFocused] = useState(false);
  let [graphLayout, setGraphLayout] = useState({ extra: null });

  useEffect(() => {
    if (graphref && graphref.current) {
      let onLayout = () => {
        if (graphref && graphref.current) {
          setGraphLayout({ extra: graphref.current.extraNodeCount() });
          setIsFocused(graphref.current.isFocused());
        }
      };
      // initialize extra number here, and set callback for future updates
      onLayout();
      graphref.current.onLayoutReady(onLayout);
    }
  }, [graphref && graphref.current]);

  let changeOrientation = (f: boolean) => {
    graphref.current && graphref.current.setVertical(f);
    setVerticalOrient(f);
  };

  let extra = graphLayout && graphLayout.extra;

  return (
    <Toolbar
      variant="dense"
      style={{
        position: "absolute",
        zIndex: 3,
        top: 10,
        right: 100,
        width: 200,
        height: 100,
      }}
    >
      {/*<ToggleButtonGroup*/}
      {/*  exclusive*/}
      {/*  value={verticalOrient}*/}
      {/*  onChange={(event, val) => changeOrientation(val)}*/}
      {/*>*/}
      {/*  <ToggleButton value={true} key="DOWN">*/}
      {/*    <Icon>keyboard_arrow_down</Icon>*/}
      {/*  </ToggleButton>*/}
      {/*  <ToggleButton value={false} key="UP">*/}
      {/*    <Icon>keyboard_arrow_right</Icon>*/}
      {/*  </ToggleButton>*/}
      {/*</ToggleButtonGroup>*/}
      {/*<Divider />*/}
      {/*<Box bgcolor="white">*/}
      {/*  <ToggleButton*/}
      {/*    value="check"*/}
      {/*    selected={showA}*/}
      {/*    onChange={() => {*/}
      {/*      graphref.current.toggleISA(!showA);*/}
      {/*      setShowA(!showA);*/}
      {/*    }}*/}
      {/*  >*/}
      {/*    <Icon>account_tree</Icon>*/}
      {/*  </ToggleButton>*/}
      {/*</Box>*/}
      {/*<Box bgcolor="white">*/}
      {/*  <ToggleButton*/}
      {/*    value="check"*/}
      {/*    selected={showE && extra > 0}*/}
      {/*    disabled={extra == 0}*/}
      {/*    onChange={() => {*/}
      {/*      graphref.current.toggleExtraNodes(!showE);*/}
      {/*      setShowE(!showE);*/}
      {/*    }}*/}
      {/*  >*/}
      {/*    <Typography>{extra ? "+" + extra : "+0  "}</Typography>*/}
      {/*  </ToggleButton>*/}
      {/*</Box>*/}
      {
        <Box bgcolor="white">
          <ToggleButton
            value="check"
            selected={showComp}
            onChange={() => {
              graphref.current.showUsedInComp(!showComp);
              setShowCalc(!showComp);
            }}
          >
            <Icon>visibility</Icon>
          </ToggleButton>
        </Box>
      }
    </Toolbar>
  );
}

const LargeButton = withStyles(() => ({
  root: {
    fontSize: 50,
  },
}))(IconButton);
const LargeIcon = withStyles(() => ({
  root: {
    fontSize: 50,
  },
}))(Icon);
